import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type Theme = "light" | "dark";
export type Sidebar = "expanded" | "collapsed";
export type Density = "comfortable" | "compact";
export type Glass = "soft" | "strong";

export interface Prefs {
  theme: Theme;
  sidebar: Sidebar;
  density: Density;
  glass: Glass;
}

const DEFAULTS: Prefs = {
  theme: "dark",
  sidebar: "expanded",
  density: "compact",
  glass: "strong",
};

const STORAGE_KEY = "control.prefs";

function load(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    return DEFAULTS;
  }
}

interface PrefsContextValue {
  prefs: Prefs;
  set<K extends keyof Prefs>(key: K, value: Prefs[K]): void;
  toggleTheme(): void;
  toggleSidebar(): void;
}

const PrefsContext = createContext<PrefsContextValue | null>(null);

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<Prefs>(() => load());

  // Persist to localStorage and reflect on <html> as data-* attrs (matches design CSS)
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    const html = document.documentElement;
    html.setAttribute("data-theme", prefs.theme);
    html.setAttribute("data-sidebar", prefs.sidebar);
    html.setAttribute("data-density", prefs.density === "comfortable" ? "comfortable" : "compact");
    html.setAttribute("data-glass", prefs.glass);
  }, [prefs]);

  const set = useCallback(<K extends keyof Prefs>(key: K, value: Prefs[K]) => {
    setPrefs((p) => ({ ...p, [key]: value }));
  }, []);

  const value = useMemo<PrefsContextValue>(() => ({
    prefs,
    set,
    toggleTheme: () => set("theme", prefs.theme === "dark" ? "light" : "dark"),
    toggleSidebar: () => set("sidebar", prefs.sidebar === "collapsed" ? "expanded" : "collapsed"),
  }), [prefs, set]);

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): PrefsContextValue {
  const v = useContext(PrefsContext);
  if (!v) throw new Error("usePrefs must be used inside <PrefsProvider>");
  return v;
}
